/**
 * Wire contracts for the staff deletion console: the overview callable and
 * the per-deletion records it and the status callable return.
 *
 * Every record is a strict object and the server parses its own output
 * against it before returning, so a field nobody listed here fails at the
 * source instead of reaching a browser. Nothing here carries a name, an
 * email, the requesting admin, audit `detail` text or job exception text;
 * the only identifier is the uid of a deletion that is still open.
 *
 * Tracking: https://github.com/CodeinPlace/cip/issues/4533
 */
import {z} from 'zod';

import {
  AccountDeletionAuditActionSchema,
  AccountDeletionSourceSchema,
  AccountDeletionStatusSchema,
} from './tombstone';

/** Deployed name of the admin-only console overview callable. */
export const GET_ACCOUNT_DELETION_OVERVIEW_CALLABLE =
  'getaccountdeletionoverview' as const;

/** The overview takes no parameters; anything sent is rejected. */
export const GetAccountDeletionOverviewRequestSchema = z.strictObject({});

/**
 * A job's status as the console reports it: the `@cip/async-job` statuses,
 * plus `unknown` for a job doc whose status the server could not read.
 * Kept here rather than imported because `@cip/core` cannot depend on
 * `@cip/async-job`; `AsyncJobStatus` there is the source of truth.
 */
export const DELETION_JOB_STATUSES = [
  'pending',
  'running',
  'complete',
  'failed',
  'cancelled',
  'unknown',
] as const;

/** See {@link DELETION_JOB_STATUSES}. */
export const DeletionJobStatusSchema = z.enum(DELETION_JOB_STATUSES);

/**
 * A machine code such as an `AsyncJobFailureKind`. The pattern admits
 * codes, not sentences, so exception text cannot pass for one.
 */
const FailureCodeSchema = z.string().regex(/^[a-z][a-z_]{0,39}$/);

/** A manifest task id, the only kind of id a task list may hold. */
const TaskIdSchema = z.string().regex(/^[a-z][a-z0-9-]{0,63}$/);

/** Epoch millis; Timestamps do not survive the callable JSON boundary. */
const EpochMsSchema = z.number().int().nonnegative();

/**
 * Health of the job running a deletion, reduced to numbers and codes. The
 * job doc's `error.message` and `recentFailures[].reason` are raw
 * exception text that can quote other users' document paths, so neither
 * is carried: only the failure kind and the ids of the tasks that failed.
 */
export const DeletionJobHealthSchema = z.strictObject({
  /** The job's status. */
  status: DeletionJobStatusSchema,
  /** Invocations the job has used, continuations included. */
  invocationCount: z.number().int().nonnegative(),
  /** Millis since the job's last heartbeat; null when it never beat. */
  heartbeatAgeMs: z.number().int().nonnegative().nullable(),
  /** The job's failure kind when it failed; null otherwise. */
  failureKind: FailureCodeSchema.nullable(),
  /** Manifest task ids with a recorded failure, deduplicated. */
  failedTaskIds: z.array(TaskIdSchema),
});

/** See {@link DeletionJobHealthSchema}. */
export type DeletionJobHealth = z.infer<typeof DeletionJobHealthSchema>;

/**
 * One deletion that is not `complete` or `cancelled`, as a console row.
 * A deletion leaves the console when it completes: a finished uid would
 * link the records the policy retains back to a person, so finished
 * deletions are only counted. A cancelled one has nothing left to act on,
 * so it is only counted too.
 */
export const OpenDeletionSchema = z.strictObject({
  /** The account's uid. Present only while the deletion is open. */
  uid: z.string().min(1),
  /** Lifecycle status; never `complete` or `cancelled`. */
  status: AccountDeletionStatusSchema.exclude(['complete', 'cancelled']),
  /** Whether the owner or an admin asked. Never who. */
  source: AccountDeletionSourceSchema,
  /** Whether a legal hold is in force. */
  legalHold: z.boolean(),
  /** When the deletion was requested; null when unreadable. */
  requestedAtMs: EpochMsSchema.nullable(),
  /** The wave it waits for; null unless `scheduled`. */
  scheduledForMs: EpochMsSchema.nullable(),
  /** When a wave or run-now dispatched it; null while `scheduled`. */
  claimedAtMs: EpochMsSchema.nullable(),
  /** Manifest tasks already checkpointed. */
  completedTaskIds: z.array(TaskIdSchema),
  /** Manifest tasks not yet checkpointed. */
  outstandingTaskIds: z.array(TaskIdSchema),
  /** True when the wave's own stall rule flags the claim. */
  isStalled: z.boolean(),
  /**
   * True when an impersonated session of this account succeeded in the
   * hour before the request. Never says who impersonated.
   */
  hasImpersonationHit: z.boolean(),
  /** The active job's health; null when no job is named. */
  job: DeletionJobHealthSchema.nullable(),
});

/** See {@link OpenDeletionSchema}. */
export type OpenDeletion = z.infer<typeof OpenDeletionSchema>;

/**
 * One hourly wave record from `healthcheck`, which has held counts only
 * since the wave stopped writing uids there.
 */
export const DeletionWaveRecordSchema = z.strictObject({
  /** When the wave ran. */
  atMs: EpochMsSchema,
  /** The record's `status` was `ok`. */
  isHealthy: z.boolean(),
  /** Scheduled tombstones the wave read. */
  scheduledFound: z.number().int().nonnegative(),
  /** Deletions the wave claimed and dispatched. */
  claimed: z.number().int().nonnegative(),
  /** Due deletions skipped for a legal hold. */
  legalHeld: z.number().int().nonnegative(),
  /** Due deletions the per-wave cap left for a later run. */
  overCap: z.number().int().nonnegative(),
  /** Claims that threw. */
  failed: z.number().int().nonnegative(),
  /** In-progress deletions the stall rule flagged. */
  stalledClaims: z.number().int().nonnegative(),
  /** Tombstones sitting in `failed`. */
  failedTombstones: z.number().int().nonnegative(),
  /** Tombstones sitting in `needs-attention`. */
  needsAttentionTombstones: z.number().int().nonnegative(),
  /** Scheduled accounts whose lockout could not be re-asserted. */
  lockoutFailed: z.number().int().nonnegative(),
});

/** See {@link DeletionWaveRecordSchema}. */
export type DeletionWaveRecord = z.infer<typeof DeletionWaveRecordSchema>;

/** One audit event on a deletion's timeline: action and task, no text. */
export const DeletionTimelineEntrySchema = z.strictObject({
  action: AccountDeletionAuditActionSchema,
  /** Manifest task id for task-scoped events; null otherwise. */
  taskId: TaskIdSchema.nullable(),
  /** Server write time; null while the timestamp is still pending. */
  atMs: EpochMsSchema.nullable(),
});

/** See {@link DeletionTimelineEntrySchema}. */
export type DeletionTimelineEntry = z.infer<typeof DeletionTimelineEntrySchema>;

/** Response from `getaccountdeletionoverview`. */
export const GetAccountDeletionOverviewResponseSchema = z.strictObject({
  /** The server instant every age and due-count was measured against. */
  generatedAtMs: EpochMsSchema,
  /** Tasks in the manifest, for progress display. */
  totalTasks: z.number().int().positive(),
  /** Tombstones per status, plus how many carry a legal hold. */
  counts: z.strictObject({
    /** Tombstones in `scheduled`. */
    scheduled: z.number().int().nonnegative(),
    /** Tombstones in `in-progress`. */
    inProgress: z.number().int().nonnegative(),
    /** Tombstones in `needs-attention`. */
    needsAttention: z.number().int().nonnegative(),
    /** Tombstones in `failed`. */
    failed: z.number().int().nonnegative(),
    /** Tombstones in `complete`; the only record finished deletions get. */
    complete: z.number().int().nonnegative(),
    /** Tombstones in `cancelled`; counted only, like `complete`. */
    cancelled: z.number().int().nonnegative(),
    /** Tombstones with `legalHold` set, whatever their status. */
    legalHold: z.number().int().nonnegative(),
  }),
  /** The next wave, and what the wave's own selection would claim then. */
  nextWave: z.strictObject({
    atMs: EpochMsSchema,
    /** Scheduled deletions due by then, capped at `cap`. */
    dueCount: z.number().int().nonnegative(),
    /** Due deletions the cap would leave for later hourly runs. */
    overCapCount: z.number().int().nonnegative(),
    /** Accounts one wave invocation claims at most. */
    cap: z.number().int().positive(),
  }),
  /** Recent hourly wave records, newest first. */
  waves: z.array(DeletionWaveRecordSchema),
  /**
   * False when the wave records could not be read, so an empty `waves`
   * reads as "unavailable" rather than "no waves ran".
   */
  isWaveHistoryAvailable: z.boolean(),
  /** Every deletion that is not `complete`, oldest request first. */
  openDeletions: z.array(OpenDeletionSchema),
  /** True when a status held more open deletions than one read returns. */
  isOpenListTruncated: z.boolean(),
  /**
   * Whether the caller may start or re-run a deletion of another account,
   * decided server-side so the page never needs the superadmin list.
   * Optional because the webapp and the functions deploy separately: a
   * response from functions that predate the field reads as "may not".
   */
  viewerCanDelete: z.boolean().optional(),
});

/** See {@link GetAccountDeletionOverviewResponseSchema}. */
export type GetAccountDeletionOverviewResponse = z.infer<
  typeof GetAccountDeletionOverviewResponseSchema
>;
